import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { workAuthorPublisherService } from "../services/workAuthorPublisherService";
import {
  WorkAuthorPublisherValuesInit,
  getWorkAuthorPublisherValues,
  WorkAuthorPublisherValuesType,
  WorkAuthorPublisherErrorsType,
  workAuthorPublisherDisplayId,
} from "./WorkAuthorPublisherCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { WorkAuthorPublisher } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getPublisherValues,
  PublisherValuesType,
  publisherDisplayId,
} from "../../publisher/store/PublisherCommonStore";
import { isNil } from "../../../common/utils";
import { publisherService } from "../../publisher/services/publisherService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class WorkAuthorPublisherUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: WorkAuthorPublisherErrorsType = {};
  service = workAuthorPublisherService;
  workAuthorPublisherInstance: WorkAuthorPublisher | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = WorkAuthorPublisherValuesInit();
  publisherError: string | undefined;
  publisherService = publisherService;
  publisherSearchValue: string = "";
  publisherOptions: PublisherValuesType[] = [];
  percentageError: string | undefined;
  durationInYearsError: string | undefined;
  autoRenewalYearsError: string | undefined;

  private customValidators: CustomValidator<WorkAuthorPublisherValuesType>[] =
    [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<WorkAuthorPublisherValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<WorkAuthorPublisherValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadWorkAuthorPublisherById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.workAuthorPublisherInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.workAuthorPublisherInstance) {
      this.entityValues = getWorkAuthorPublisherValues(
        this.workAuthorPublisherInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda.
  get saveDisabled() {
    return this.isProcessing;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return workAuthorPublisherDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    if (this.workAuthorPublisherInstance) {
      return workAuthorPublisherDisplayId(
        this.workAuthorPublisherInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.WorkAuthorPublisherSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupWorkAuthorPublisherLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity

      if (callerData?.fieldName === "publisher") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            publisherDisplayId(returnData.entity, this.langStore);
          this.entityValues.showPublisher = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.publisherOne({ id: returnData.resultId });
          const publisher = result.publisherOne;
          this.entityValues.showPublisher = {
            displayId: publisher.displayId,
            id: publisher.id,
          };
          this.setPublisherOptions([
            getPublisherValues(publisher, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  setPublisherOptions(values: PublisherValuesType[]) {
    this.publisherOptions = values;
  }

  *loadPublisherValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Publisher");
    const allOptions = cache.getOptions("Publisher") as PublisherValuesType[];

    const searchValue = this.publisherSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setPublisherOptions(filtered);
  }

  getPublisherOptions() {
    return this.publisherOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPublisherValue(value: string) {
    this.publisherSearchValue = value;
    yield this.loadPublisherValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPublisher(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewPublisher(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "publisher"
      );
    } else {
      if (value) {
        this.entityValues.showPublisher = value;
        this.entityValues.__publisher = value.displayId;
      }
    }
  }

  getPercentageValue(): number {
    return this.entityValues.percentage;
  }

  setPercentageValue(value: number) {
    this.entityValues.percentage = value;
  }

  getDurationInYearsValue(): number | undefined {
    return this.entityValues.durationInYears;
  }

  setDurationInYearsValue(value: number | undefined) {
    this.entityValues.durationInYears = value;
  }

  getAutoRenewalYearsValue(): number | undefined {
    return this.entityValues.autoRenewalYears;
  }

  setAutoRenewalYearsValue(value: number | undefined) {
    this.entityValues.autoRenewalYears = value;
  }

  getRowFromInstance(instance: WorkAuthorPublisher) {
    const row: any = {};
    row.version_ = instance.version_;
    row.id = instance.id;
    row.percentage = instance.percentage;
    row.durationInYears = instance.durationInYears;
    row.autoRenewalYears = instance.autoRenewalYears;
    return row;
  }

  addNewPublisher(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "WorkAuthorPublisher",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/publisher/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.workAuthorPublisherInstance = null;
    this.entityValues = WorkAuthorPublisherValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.publisherSearchValue = "";
    this.publisherOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(
    values: WorkAuthorPublisherValuesType
  ): WorkAuthorPublisherErrorsType {
    const errors: WorkAuthorPublisherErrorsType = {};
    this.clearErrors();
    this.publisherError = undefined;
    if (values.showPublisher === null || values.showPublisher.id === "") {
      errors.publisher = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorPublisherPublisherLabel,
      };
    }
    if (values.percentage > 33) {
      errors.percentage = {
        message: this.langStore.maxValue + ": 33",
        fieldLabel: this.langStore.WorkAuthorPublisherPercentageLabel,
      };
    }
    if (values.percentage < 1) {
      errors.percentage = {
        message: this.langStore.minValue + ": 1",
        fieldLabel: this.langStore.WorkAuthorPublisherPercentageLabel,
      };
    }

    if (isNil(values.percentage)) {
      errors.percentage = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorPublisherPercentageLabel,
      };
    }
    if (values.durationInYears != null && values.durationInYears > 100) {
      errors.durationInYears = {
        message: this.langStore.maxValue + ": 100",
        fieldLabel: this.langStore.WorkAuthorPublisherDurationInYearsLabel,
      };
    }
    if (values.durationInYears != null && values.durationInYears < 1) {
      errors.durationInYears = {
        message: this.langStore.minValue + ": 1",
        fieldLabel: this.langStore.WorkAuthorPublisherDurationInYearsLabel,
      };
    }
    if (values.autoRenewalYears != null && values.autoRenewalYears > 50) {
      errors.autoRenewalYears = {
        message: this.langStore.maxValue + ": 50",
        fieldLabel: this.langStore.WorkAuthorPublisherAutoRenewalYearsLabel,
      };
    }
    if (values.autoRenewalYears != null && values.autoRenewalYears < 1) {
      errors.autoRenewalYears = {
        message: this.langStore.minValue + ": 1",
        fieldLabel: this.langStore.WorkAuthorPublisherAutoRenewalYearsLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "WorkAuthorPublisherUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      percentage: this.entityValues.percentage,
      durationInYears: this.entityValues.durationInYears,
      autoRenewalYears: this.entityValues.autoRenewalYears,
      publisher: this.entityValues.showPublisher?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveWorkAuthorPublisher(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            this.resetPendingChanges();
            this.saveOriginalState();
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelWorkAuthorPublisher(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getWorkAuthorPublisherValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = WorkAuthorPublisherValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    // Subentidad sin edición standalone: se persiste por el update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadWorkAuthorPublisherById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadPublisherValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
